<template>
  <div
    v-if="isLoyaltyDiscountEnabled"
    class="flex flex-col gap-1"
    data-testid="partners-summary"
  >
    <div class="flex items-start justify-between gap-4">
      <CustomLink
        v-if="currentLoyaltyLevel"
        :url="dashboardUrl"
        :type="LINK_TYPE.NoStyle"
        class="shrink-0"
        data-testid="partners-summary-dashboard-link"
      >
        <DiscountCategoryLabel
          :category="DISCOUNT_CATEGORY.LOYALTY"
          :loyalty-level="currentLoyaltyLevel"
          :level-title="$t(LOYALTY_LEVEL_TITLES[currentLoyaltyLevel.levelNumber])"
          is-active
        />
      </CustomLink>
      <transition name="sf-fade">
        <PartnersPointsStatus
          v-if="hasPoints && loyaltyPoints"
          :loyalty-points="loyaltyPoints"
          class="items-end text-right"
        />
      </transition>
    </div>
    <SectionTransition
      :has-content="hasPoints && nextLevelNumber !== null"
      client-only
    >
      <div
        v-if="loyaltyPoints && nextLevelNumber !== null && loyaltyPoints.pointsToNextLevel !== null"
        class="text-xs"
        data-testid="partners-summary-next-level"
      >
        {{ $t('partners-summary-points-to-level', {
          points: formatPoints(loyaltyPoints.pointsToNextLevel),
          levelName: $t(LOYALTY_LEVEL_TITLES[nextLevelNumber]),
        }, loyaltyPoints.pointsToNextLevel) }}
      </div>
    </SectionTransition>
  </div>
</template>

<script lang="ts">

import DiscountCategoryLabel from '~/components/atoms/DiscountCategoryLabel/DiscountCategoryLabel.vue';
import CustomLink from '~/components/atoms/Link/CustomLink.vue';
import PartnersPointsStatus from '~/components/molecules/PartnersPointsStatus/PartnersPointsStatus.vue';
import { DISCOUNT_CATEGORY } from '~~/types/discounts';
import { LINK_TYPE } from '~~/types/components/Link/LinkType';
import { ROUTES } from '~~/constants/routes';
import { LOYALTY_LEVEL_TITLES } from '~~/constants/loyaltyProgram';
import { getNextLoyaltyLevelNumber } from '~~/helpers/loyaltyPoints/getNextLoyaltyLevelNumber';

export default defineComponent({
  name: 'PartnersSummary',
  components: {
    DiscountCategoryLabel,
    CustomLink,
    PartnersPointsStatus,
  },
  setup() {

    const i18n = useI18n();
    const { isLoyaltyDiscountEnabled } = useStoreVsfExtended();
    const { currentLoyaltyLevel, loadLoyaltyLevels } = useCurrentLoyaltyLevel();
    const { loyaltyPoints, error } = useLoyaltyPoints();
    const { getUrlByRouteKey } = useRouteExtended();

    const dashboardUrl = getUrlByRouteKey(ROUTES.MY_EXPONDO_PARTNERS);

    const hasPoints = computed(() => !!loyaltyPoints.value && !error.value);

    const nextLevelNumber = computed(() =>
      loyaltyPoints.value ? getNextLoyaltyLevelNumber(loyaltyPoints.value) : null,
    );

    const formatPoints = (points: number): string => i18n.n(points);

    onMounted(() => {

      loadLoyaltyLevels();

    });

    return {
      DISCOUNT_CATEGORY,
      LINK_TYPE,
      LOYALTY_LEVEL_TITLES,
      isLoyaltyDiscountEnabled,
      currentLoyaltyLevel,
      loyaltyPoints,
      hasPoints,
      nextLevelNumber,
      dashboardUrl,
      formatPoints,
    };

  },
});

</script>
